import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Styles } from "./postJob";
import { colors } from "../../../utils/Colors";
import Footer from "../../../components/common/Footer";
import Header from "../../../components/common/Header";
import PostJobForm from "../../../components/Jobs/PostJobForm";
import Search from "../../../assets/images/postjob-img.svg";
import Categories from "../../../components/common/Categories";
import { checkJobPostEligibility } from "../../../api/subscriptions.api";
import Paths from "../../../utils/Paths";

const c = colors.color;

const PostJob = () => {
  const navigate = useNavigate();

  const [eligibility, setEligibility] = useState(null); // null = loading
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    checkJobPostEligibility()
      .then((res) => setEligibility(res.data))
      .catch((err) => {
        console.error("Eligibility check failed:", err);
        setLoadError(true);
      });
  }, []);

  const isLoading = eligibility === null && !loadError;
  const isBlocked = eligibility && !eligibility.allowed;
  const isQuotaExceeded = eligibility?.reason === "quota_exceeded";

  return (
    <Styles colors={colors}>
      <Header />
      <Categories />

      <div className="post-job-section">
        <section>
          <h3>Job Description</h3>

          {/* ── Loading ── */}
          {isLoading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <div
                className="spinner-border"
                role="status"
                style={{ color: c.secondary, width: 40, height: 40 }}
              />
            </div>
          )}

          {/* ── API error fallback ── */}
          {loadError && (
            <div
              style={{
                background: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: 12,
                padding: "20px 24px",
                color: "#856404",
                fontSize: 15,
              }}
            >
              ⚠️ Failed to check your posting eligibility. Please refresh the page and try again.
            </div>
          )}

          {/* ── BLOCKED: no subscription or quota exceeded ── */}
          {!isLoading && !loadError && isBlocked && (
            <div
              style={{
                background: "#fff",
                border: `2px solid ${isQuotaExceeded ? "#f1b44c" : c.secondary}`,
                borderRadius: 16,
                padding: "40px 32px",
                textAlign: "center",
                maxWidth: 500,
                boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
              }}
            >
              {/* Icon */}
              <div
                style={{
                  width: 90,
                  height: 90,
                  borderRadius: "50%",
                  background: isQuotaExceeded
                    ? "rgba(241,180,76,0.12)"
                    : `${c.secondary}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px",
                  fontSize: 42,
                }}
              >
                {isQuotaExceeded ? "📊" : "🔒"}
              </div>

              {/* Title */}
              <h4
                style={{
                  fontWeight: 800,
                  color: c.primary,
                  marginBottom: 12,
                  fontSize: 22,
                }}
              >
                {isQuotaExceeded
                  ? "Monthly Job Post Limit Reached"
                  : "Subscription Required"}
              </h4>

              {/* Message from API */}
              <p
                style={{
                  color: c.darkGrey,
                  fontSize: 15,
                  lineHeight: 1.7,
                  marginBottom: 24,
                }}
              >
                {eligibility.message}
              </p>

              {/* Quota bar — quota exceeded only */}
              {isQuotaExceeded && (
                <div style={{ marginBottom: 24, textAlign: "left" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 6,
                    }}
                  >
                    <span style={{ fontSize: 13, color: c.darkGrey }}>
                      Job posts used this month
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#f46a6a",
                      }}
                    >
                      {eligibility.jobsUsed} / {eligibility.jobsLimit}
                    </span>
                  </div>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 4,
                      background: c.lightGrey,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: "100%",
                        background: "linear-gradient(90deg, #f1b44c, #f46a6a)",
                        borderRadius: 4,
                      }}
                    />
                  </div>
                  {eligibility.periodEnd && (
                    <p
                      style={{ fontSize: 12, color: c.darkGrey, marginTop: 6 }}
                    >
                      Resets on{" "}
                      <strong style={{ color: c.primary }}>
                        {new Date(eligibility.periodEnd).toLocaleDateString()}
                      </strong>
                      {eligibility.planName && (
                        <>
                          {" "}
                          · Current plan:{" "}
                          <strong style={{ color: c.primary }}>
                            {eligibility.planName}
                          </strong>
                        </>
                      )}
                    </p>
                  )}
                </div>
              )}

              {/* Perks */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "12px 28px",
                  flexWrap: "wrap",
                  marginBottom: 28,
                }}
              >
                {[
                  { icon: "📋", text: "Multiple plans available" },
                  { icon: "⚡", text: "Instant activation" },
                  { icon: "↩️", text: "Cancel anytime" },
                ].map((p) => (
                  <div
                    key={p.text}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 13,
                      color: c.darkGrey,
                    }}
                  >
                    <span>{p.icon}</span>
                    <span>{p.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <button
                onClick={() => navigate(Paths.subscriptionPlans)}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: 12,
                  border: "none",
                  background: `linear-gradient(135deg, ${c.secondary}, #00a67e)`,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: "pointer",
                  boxShadow: `0 6px 20px ${c.secondary}44`,
                  letterSpacing: "0.3px",
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                {isQuotaExceeded
                  ? "⬆️ Upgrade to a Higher Plan"
                  : "🚀 View Subscription Plans"}
              </button>
            </div>
          )}

          {/* ── ALLOWED: show the form ── */}
          {!isLoading && !loadError && !isBlocked && <PostJobForm />}
        </section>

        <div className="image-section">
          <div className="search">
            <img src={Search} alt="search" />
          </div>
        </div>
      </div>

      <Footer />
    </Styles>
  );
};

export default PostJob;